<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :baseZIndex="1200"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div class="crc-form-dialog-header__icon bg-violet-50 dark:bg-violet-500/15 text-violet-600 dark:text-violet-400">
          <i class="pi pi-chart-bar text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ modalTitle }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section crc-form-dialog-fields">
        <FloatLabel variant="on" class="w-full">
          <InputText
            id="grade-edit-name"
            v-model="draft.name"
            :disabled="saving"
            maxlength="120"
            autocomplete="off"
            :invalid="submitted && !draft.name?.trim()"
            class="w-full rounded-md"
          />
          <label for="grade-edit-name" class="text-sm font-medium">
            Grade Name <span class="text-red-500">*</span>
          </label>
        </FloatLabel>
        <small v-if="submitted && !draft.name?.trim()" class="text-xs text-red-500 pl-1 -mt-2">
          Please enter a grade name.
        </small>

        <FloatLabel v-if="mode === 'edit'" variant="on" class="w-full">
          <InputNumber
            inputId="grade-edit-level"
            v-model="draft.level"
            :disabled="saving"
            :min="1"
            :max="9999"
            :useGrouping="false"
            class="w-full"
            inputClass="w-full rounded-md"
          />
          <label for="grade-edit-level" class="text-sm font-medium">Level</label>
        </FloatLabel>
        <p v-else class="text-xs text-gray-500 dark:text-gray-400 m-0">
          Level is assigned automatically on create.
        </p>
      </section>

      <section v-if="mode === 'edit'" class="crc-form-dialog-section">
        <div class="crc-form-dialog-status">
          <div class="flex items-center gap-2.5">
            <div
              class="crc-form-dialog-status__dot"
              :class="draft.is_active ? 'crc-form-dialog-status__dot--active' : 'crc-form-dialog-status__dot--inactive'"
            />
            <div>
              <div class="crc-form-dialog-status__title">
                {{ draft.is_active ? 'Active' : 'Inactive' }}
              </div>
              <div class="crc-form-dialog-status__hint">
                Inactive grades are hidden from profile options.
              </div>
            </div>
          </div>
          <ToggleSwitch inputId="grade-active" v-model="draft.is_active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <Button
          v-if="draft.id && canDelete"
          label="Delete"
          icon="pi pi-trash"
          severity="danger"
          text
          :disabled="saving || !canDelete"
          @click="confirmDelete"
        />
        <div class="crc-form-dialog-footer__actions">
          <Button
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="onClose"
          />
          <Button
            :label="mode === 'new' ? 'Create' : 'Save'"
            :icon="mode === 'new' ? 'pi pi-plus' : 'pi pi-save'"
            :loading="saving"
            :disabled="saving || !canEdit || !isFormValid"
            type="submit"
          />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import ToggleSwitch from 'primevue/toggleswitch';
import FloatLabel from 'primevue/floatlabel';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';

const emit = defineEmits(['update:visible', 'save', 'delete']);

const props = defineProps({
  visible: { type: Boolean, default: false },
  grade: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: true },
  canDelete: { type: Boolean, default: true },
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const mode = ref('edit');
const submitted = ref(false);

const draft = reactive({
  id: null,
  name: '',
  level: null,
  is_active: true,
});

const modalTitle = computed(() => (mode.value === 'new' ? 'Add Grade' : 'Edit Grade'));

const isFormValid = computed(() => {
  if (!draft.name?.trim()) return false;
  if (mode.value === 'edit' && (draft.level == null || draft.level < 1)) return false;
  return true;
});

const fillDraftFromGrade = (grade) => {
  if (!grade) {
    fillDraftNew();
    return;
  }
  draft.id = grade.id ?? null;
  draft.name = grade.name ?? '';
  draft.level = grade.level ?? null;
  draft.is_active = grade.is_active ?? true;
};

const fillDraftNew = () => {
  draft.id = null;
  draft.name = '';
  draft.level = null;
  draft.is_active = true;
};

watch(
  () => props.grade,
  (value) => {
    if (visibleProxy.value && mode.value === 'edit') fillDraftFromGrade(value);
  },
  { deep: true }
);

watch(
  () => props.visible,
  (open) => {
    if (!open) return;
    submitted.value = false;
    if (props.grade) {
      mode.value = 'edit';
      fillDraftFromGrade(props.grade);
    } else {
      mode.value = 'new';
      fillDraftNew();
    }
  }
);

const onClose = () => {
  if (props.saving) return;
  visibleProxy.value = false;
  submitted.value = false;
};

const handleSubmit = () => {
  submitted.value = true;
  if (!isFormValid.value || props.saving) return;
  emit('save', { ...draft });
  visibleProxy.value = false;
  submitted.value = false;
};

const confirmDelete = () => {
  if (!draft.id || !props.canDelete || props.saving) return;
  const ok = window.confirm('Delete this grade? This action cannot be undone.');
  if (!ok) return;
  emit('delete', { id: draft.id });
  visibleProxy.value = false;
};
</script>
